import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useGetMissingDynamicAssigneesCount } from '@omnea-app/fe-budget-approvals/useBudgetApprovals'
import { useListWorkflows } from '@omnea-app/fe-reporting/useListWorkflows'
import { WorkflowStateEnum } from '@omnea-app/packages/universal/workflow/workflowEnums'

type OrgConfigIssue = {
  count: number
  calloutKey: string
  href: string
}

export type OrgConfigIssueCallout = {
  text: string
  href: string
}

export const useOrgConfigIssuesBannerInfo = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthenticatedUser()

  const { data: workflowsData, isLoading: isWorkflowsLoading } = useListWorkflows({
    skip: !user.isAdmin,
    state: [WorkflowStateEnum.UNDER_MAINTENANCE],
  })
  const { data: missingAssigneesData, isLoading: isMissingAssigneesLoading } = useGetMissingDynamicAssigneesCount({
    skip: !user.isAdmin,
  })

  const isLoading = isWorkflowsLoading || isMissingAssigneesLoading

  const issues: OrgConfigIssue[] = [
    {
      count: isWorkflowsLoading ? 0 : (workflowsData?.workflows.length ?? 0),
      calloutKey: 'org-config-issues-broken-workflows',
      href: '/settings/workflows',
    },
    {
      count: isMissingAssigneesLoading ? 0 : (missingAssigneesData?.count ?? 0),
      calloutKey: 'org-config-issues-missing-assignees',
      href: '/settings/dynamic-assignees',
    },
  ]

  const activeIssues = issues.filter((issue) => issue.count > 0)
  const count = activeIssues.reduce((total, issue) => total + issue.count, 0)

  const callouts: OrgConfigIssueCallout[] = activeIssues.map((issue) => ({
    text: t(issue.calloutKey, { count: issue.count }),
    href: issue.href,
  }))

  return {
    shouldShowBanner: user.isAdmin && activeIssues.length > 0,
    title: t('org-config-issues-banner'),
    callouts,
    count,
    isLoading,
  }
}
